import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Tabs } from '@vinted/web-ui';
import classNames from 'classnames';
import useTracking from 'hooks/useTracking';
import useTranslate from 'hooks/useTranslate';
import { ABOUT_TAB_ID, OUR_PLATFORM_ID, ROOT_CATALOG_ID } from 'constants/catalog';
import { ITEM_UPLOAD_URL, MAIN_NAVIGATION_ABOUT_TAB_URL, OUR_PLATFORM_URL } from 'constants/routes';
import { ClickableElement } from 'constants/tracking/clickable-elements';
import { transformCatalogDtoList } from 'data/transformers/catalog';
import { clickEvent } from 'libs/common/event-tracker/events';
import { navigateToPage } from 'libs/utils/window';
import AboutPanel from './AboutPanel';
import CategoriesPanel from './CategoriesPanel';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const CatalogNavigation = _ref => {
  let {
    tree,
    impressumUrl,
    isBusinessAccountLinksVisible,
    isOurPlatformVisible
  } = _ref;
  const translate = useTranslate('header.main_navigation');
  const {
    track
  } = useTracking();
  const {
    0: activeTabId,
    1: setActiveTabId
  } = useState(ROOT_CATALOG_ID);
  const {
    0: isModalOpen,
    1: setIsModalOpen
  } = useState(false);
  const {
    0: modalContent,
    1: setModalContent
  } = useState({
    catalogName: '',
    imageUrl: '',
    itemsRemaining: 0
  });
  const transformedTree = useMemo(() => transformCatalogDtoList(tree), [tree]);

  const openCatalogModal = _ref2 => {
    let {
      catalogName,
      itemsRemaining,
      imageUrl
    } = _ref2;
    setModalContent({
      catalogName,
      itemsRemaining,
      imageUrl
    });
    setIsModalOpen(true);
  };

  const closeCatalogModel = () => {
    setModalContent({
      catalogName: '',
      imageUrl: '',
      itemsRemaining: 0
    });
    setIsModalOpen(false);
  };

  const handleTabChange = (_ref3, event) => {
    let {
      id
    } = _ref3;
    event.nativeEvent.stopImmediatePropagation();

    if (id === activeTabId) {
      setActiveTabId(ROOT_CATALOG_ID);
    } else {
      setActiveTabId(id);
    }

    if (id === OUR_PLATFORM_ID) {
      navigateToPage(OUR_PLATFORM_URL);
    }
  };

  const handleTitleClick = event => {
    event.preventDefault();
  };

  const renderTabTitle = (url, title, testId) => /*#__PURE__*/_jsx("a", {
    className: "nav-links",
    role: "button",
    href: url,
    onClick: handleTitleClick,
    "data-testid": testId,
    tabIndex: -1,
    children: title
  });

  const renderTabContent = item => /*#__PURE__*/_jsx("div", {
    id: "nav-menu-".concat(item.id),
    "aria-hidden": item.id !== activeTabId,
    "data-testid": "category-panel-".concat(item.id),
    className: classNames('u-padding-none', {
      'is-active': item.id === activeTabId
    }),
    children: /*#__PURE__*/_jsx(CategoriesPanel, {
      rootCategory: item,
      triggerCatalogModal: openCatalogModal
    })
  }, item.id);

  const renderAboutTabContent = isAboutPanelActive => /*#__PURE__*/_jsx("div", {
    id: "nav-menu-about",
    "aria-hidden": !isAboutPanelActive,
    "data-testid": "category-panel-about",
    className: classNames('u-padding-none', {
      'is-active': isAboutPanelActive
    }),
    children: /*#__PURE__*/_jsx(AboutPanel, {
      impressumUrl: impressumUrl,
      isBusinessAccountLinksVisible: isBusinessAccountLinksVisible
    })
  });

  const getTabItems = () => {
    const newList = [];
    const isAboutPanelActive = activeTabId === ABOUT_TAB_ID;
    transformedTree.forEach(item => {
      newList.push({
        id: item.id,
        title: renderTabTitle(item.url, item.title, "nav-tab-".concat(item.id)),
        aria: {
          'aria-expanded': activeTabId === item.id,
          'aria-controls': "nav-menu-".concat(item.id),
          'aria-label': item.title
        },
        contentExperimental: renderTabContent(item)
      });
    });
    newList.push({
      id: ABOUT_TAB_ID,
      title: renderTabTitle(MAIN_NAVIGATION_ABOUT_TAB_URL, translate('tabs.about'), 'nav-tab-about'),
      aria: {
        'aria-expanded': isAboutPanelActive,
        'aria-controls': 'nav-menu-about',
        'aria-label': translate('tabs.about')
      },
      contentExperimental: renderAboutTabContent(isAboutPanelActive)
    });

    if (isOurPlatformVisible) {
      newList.push({
        id: OUR_PLATFORM_ID,
        title: renderTabTitle(OUR_PLATFORM_URL, translate('tabs.platform'), 'nav-tab-platform'),
        aria: {
          'aria-label': translate('tabs.platform')
        }
      });
    }

    return newList;
  };

  const handleClick = useCallback(() => {
    setActiveTabId(ROOT_CATALOG_ID);
  }, []);
  useEffect(() => {
    if (activeTabId !== ROOT_CATALOG_ID) {
      document.addEventListener('click', handleClick);
    }

    return () => document.removeEventListener('click', handleClick);
  }, [activeTabId, handleClick]);
  useEffect(() => {
    if (activeTabId === ROOT_CATALOG_ID || activeTabId === ABOUT_TAB_ID || activeTabId === OUR_PLATFORM_ID) return;
    const trakingEvent = clickEvent({
      target: ClickableElement.CatalogTab,
      targetDetails: "".concat(activeTabId)
    });
    track(trakingEvent);
  }, [activeTabId, track]);
  const title = translate('catalogs.new.title', {
    name: modalContent.catalogName
  });
  const description = translate('catalogs.new.details', {
    count: modalContent.itemsRemaining
  }, {
    count: modalContent.itemsRemaining
  });
  const imageUrl = modalContent.imageUrl || undefined;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("nav", {
      className: "u-fit-content-width",
      children: /*#__PURE__*/_jsx(Tabs, {
        items: getTabItems(),
        activeItemId: activeTabId,
        onClick: handleTabChange,
        role: "menu"
      })
    }), /*#__PURE__*/_jsx(Modal, {
      show: isModalOpen,
      closeOnOverlay: true,
      defaultCallback: closeCatalogModel,
      imageUrl: imageUrl,
      title: title,
      body: description,
      actions: [{
        text: translate('catalogs.new.actions.upload'),
        style: Modal.ActionStyling.Filled,
        url: ITEM_UPLOAD_URL
      }, {
        text: translate('catalogs.new.actions.close'),
        style: Modal.ActionStyling.Flat,
        callback: closeCatalogModel
      }]
    })]
  });
};

export default CatalogNavigation;